<template>
  <svg ref="el" :viewBox="`0 0 ${svgWidth} ${svgHeight}`">
    <mask :id="maskId">
      <rect :width="svgWidth" :height="svgHeight" fill="black" />
      <rect :width="svgWidth" :height="svgHeight" fill="white" :rx="radius" />
    </mask>
    <g :mask="`url(#${maskId})`">
      <TransitionGroup leave-to-class="!-translate-y-full">
        <g
          v-for="(item, index) in cards"
          :key="item.key"
          class="transition duration-700 ease-bounce"
          :style="{
            transform: `translateY(${Math.min(stackSize - 1 - index, stackSize - 1) * translateY}px)`,
            transitionDelay: (stackSize - index) * 90 + 'ms',
          }"
        >
          <rect
            :width="svgWidth"
            :height="cardHeight"
            :fill="item.color"
            :rx="radius"
          />

          <foreignObject x="0" y="0" :width="svgWidth" :height="cardHeight">
            <p
              class="p-6 leading-none font-bold"
              :class="
                item.isFirst
                  ? 'text-[73px] text-black'
                  : 'text-size-2 text-white'
              "
              v-html="item.name"
            />
          </foreignObject>
        </g>
      </TransitionGroup>
    </g>
  </svg>
</template>

<script setup lang="ts">
import { useElementVisibility, useRafFn } from '@vueuse/core'
import { ASPECT_RATIO, CARD_COVER_BG } from '~/helpers/zurichCard'

const props = defineProps<{
  id: string
  noAnimation?: boolean
}>()

const data = await useZurichCardServices()

const maskId = computed(() => 'mask-' + props.id)

const items = computed(() =>
  data.value.map((card) => ({
    key: card.id,
    name: card.name,
    color: card.isCover ? CARD_COVER_BG : card.color,
    isFirst: card.isCover,
  })),
)

const stackSize = 4 // How many cards to render in the stack.
const duration = 3000 // How long a card should be visible in milliseconds.
const svgWidth = 400 // The width of the SVG.
const svgHeight = 300 // The height of the SVG.
const radius = 25 // The border radius of a card.

// The height of a single card.
const cardHeight = Math.round(svgWidth / ASPECT_RATIO)

// How much to translate the card on the Y axis.
const translateY = (svgHeight - cardHeight) / (stackSize - 1)

// The current animation index.
const currentIndex = ref(0)

const el = ref<SVGElement>()
const isVisible = useElementVisibility(el)
const startTimestamp = performance.now()

const cards = computed(() => {
  const normalizedIndex = currentIndex.value % items.value.length

  return [
    ...items.value.slice(normalizedIndex),
    ...items.value.slice(0, normalizedIndex),
  ]
    .slice(0, stackSize)
    .reverse() // Reverse it so they're rendered in the right order, as there is no z-index in an SVG.
})

useRafFn((ctx) => {
  if (!isVisible.value || props.noAnimation) {
    return
  }
  currentIndex.value = Math.floor((ctx.timestamp - startTimestamp) / duration)
})
</script>
